import { apiService, apiUploadService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';
import {
  IProviderRateDocument,
  IProviderRateDocumentReview,
  IConfirmRateRow,
  IConfirmRateSheetResult,
  IProviderRateItem,
  IUpsertRateItemRequest,
} from '../interfaces/heavyDutyMachinery/IProviderRateSheet';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL;

/**
 * A provider's own price list and the catalogue it becomes.
 *
 * Everything here is scoped to the caller server-side. There is no "read
 * somebody else's rate card" call and there should never be one: what a
 * tradesperson buys at and marks up is their commercial position.
 */

/**
 * Returns as soon as the file is stored, with the document in `Analyzing`.
 * The reading happens server-side within the minute -- poll `getRateSheet`
 * rather than expecting this to come back parsed.
 */
export const uploadRateSheet = async (
  file: File,
  pricesIncludeVat: boolean,
  currencyCode = 'EUR',
): Promise<IProviderRateDocument> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('pricesIncludeVat', String(pricesIncludeVat));
  formData.append('currencyCode', currencyCode);

  const response = await apiUploadService<IProviderRateDocument>({
    url: API_ENDPOINTS.PROVIDER_RATE_SHEETS,
    formData,
    baseUrl: heavyDutyMachineryBaseUrl,
  });

  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to upload the price list');
};

export const getMyRateSheets = async (): Promise<IProviderRateDocument[]> => {
  const response = await apiService<IProviderRateDocument[]>({
    url: API_ENDPOINTS.PROVIDER_RATE_SHEETS,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load your price lists');
};

/** The document plus the parsed rows, already sorted worst-confidence first by the server. */
export const getRateSheet = async (id: number): Promise<IProviderRateDocumentReview> => {
  const response = await apiService<IProviderRateDocumentReview>({
    url: API_ENDPOINTS.PROVIDER_RATE_SHEET_BY_ID(id),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the price list');
};

/**
 * The only call that writes prices a quote can be built from. Send the rows
 * exactly as the provider left them, with VAT-inclusive prices.
 */
export const confirmRateSheet = async (id: number, rows: IConfirmRateRow[]): Promise<IConfirmRateSheetResult> => {
  const response = await apiService<IConfirmRateSheetResult>({
    url: API_ENDPOINTS.PROVIDER_RATE_SHEET_CONFIRM(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { rows },
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to confirm the price list');
};

/** Removes the document, never the catalogue it produced. */
export const deleteRateSheet = async (id: number): Promise<void> => {
  const response = await apiService<void>({
    url: API_ENDPOINTS.PROVIDER_RATE_SHEET_BY_ID(id),
    method: HTTP_METHODS.DELETE,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to delete the price list');
};

export const getMyRateItems = async (search?: string, kind?: string): Promise<IProviderRateItem[]> => {
  const params: Record<string, string> = {};
  if (search) params.search = search;
  if (kind) params.kind = kind;

  const response = await apiService<IProviderRateItem[]>({
    url: API_ENDPOINTS.PROVIDER_RATE_ITEMS,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
    params,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load your catalogue');
};

export const createRateItem = async (request: IUpsertRateItemRequest): Promise<IProviderRateItem> => {
  const response = await apiService<IProviderRateItem>({
    url: API_ENDPOINTS.PROVIDER_RATE_ITEMS,
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { ...request },
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to save the entry');
};

/**
 * Editing a price here is what makes it survive the next upload: the server
 * flips its source to `Corrected`, and a later price list leaves it alone.
 */
export const updateRateItem = async (id: number, request: IUpsertRateItemRequest): Promise<IProviderRateItem> => {
  const response = await apiService<IProviderRateItem>({
    url: API_ENDPOINTS.PROVIDER_RATE_ITEM_BY_ID(id),
    method: HTTP_METHODS.PUT,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { ...request },
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to save the entry');
};

export const deleteRateItem = async (id: number): Promise<void> => {
  const response = await apiService<void>({
    url: API_ENDPOINTS.PROVIDER_RATE_ITEM_BY_ID(id),
    method: HTTP_METHODS.DELETE,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to delete the entry');
};
